import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckIcon, ShieldCheck, Loader2, CheckCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import stripeBlackLogo from "@/assets/stripe_black.svg";
import cardTypes from "@/assets/card_types.png";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { auth, createDirectSubscription, createOneTimePayment } from "@/data/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { signOut } from "firebase/auth";
import { useUser } from "../../contexts/UserContext";
import { trackPixelEvent } from "../../lib/hyros";

const features = [
  "Unlimited reply generations",
  "Extract texts from screenshots",
  "Analyze every chat",
  "Realtime personalized coaching",
  "Battle-tested replies",
  "Built by real dating coaches enhanced by AI",
  "Mobile and desktop app",
];

export function NewRegister() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackPixelEvent("AddToCart", { currency: "USD", value: 39.0 });
  }, []);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [accountHasAccess, setAccountHasAccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const nameInput = form.querySelector("#name") as HTMLInputElement;
    const name = nameInput.value;

    if (!name || !name.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!cardComplete) {
      setError("Please complete card details");
      return;
    }

    if (cardError) {
      setError(cardError);
      return;
    }

    const cardElement = elements?.getElement(CardElement);
    if (!cardElement) {
      setError(
        "Card element not found. Please refresh the page and try again."
      );
      return;
    }

    setIsProcessing(true);
    setError(null);

    if (!stripe || !elements || !email) {
      setError(
        "Stripe or user details are not available. Please refresh and try again."
      );
      setIsProcessing(false);
      return;
    }

    try {
      // 1. Create PaymentMethod
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: name,
            email: email,
          },
        });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || "Failed to create payment method.");
      }

      // 2. Call createOneTimePayment
      const directSubResult = await createOneTimePayment({
        paymentMethodId: paymentMethod.id,
        name: name,
        email: email,
      });

      // console.log("directSubResult", directSubResult);

      // Type assertion for the result data
      const resultData = directSubResult.data as {
        success: boolean;
        requiresAction?: boolean;
        paymentIntentClientSecret?: string;
        paymentIntentId?: string;
        message?: string;
      };

      if (resultData.requiresAction && resultData.paymentIntentClientSecret) {
        // 3. Handle 3D Secure or other actions
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(resultData.paymentIntentClientSecret);

        if (confirmError) {
          throw new Error(
            confirmError.message || "Payment confirmation failed."
          );
        }

        if (paymentIntent?.status === "succeeded") {
          // Payment successful after action
          trackPixelEvent("Purchase", { currency: "USD", value: 39.0 });
          setDisplaySuccess(true);
        } else {
          // Payment not successful after action
          throw new Error(
            `Payment not successful after confirmation: ${paymentIntent?.status}`
          );
        }
      } else if (resultData.success) {
        // Payment successful without further action
        trackPixelEvent("Purchase", { currency: "USD", value: 39.0 });
        setDisplaySuccess(true);
      } else {
        // Payment failed
        throw new Error(
          resultData.message || "Payment failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Payment error:", error);
      const code = (error as { details?: { code?: string } })?.details?.code;
      if (code === "account_has_access") {
        setAccountHasAccess(true);
        setError(null);
        setIsProcessing(false);
        return;
      }
      setError(
        error instanceof Error
          ? error.message
          : "An error occurred during payment processing."
      );
      setIsProcessing(false);
    }
  };

  if (displaySuccess) {
    return (
      <Navigate
        to={`/register/confirmation?email=${encodeURIComponent(email)}`}
      />
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-left">
          <CardTitle className="text-xl">
            1 Month Access Pass - 50% Off
          </CardTitle>
          <CardDescription className="text-gray-700">
            Just $39. One time payment. Full access for 1 month.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6">
            <div className="grid gap-4">
              <form
                onSubmit={handleSubmit}
                className="space-y-0 sm:space-y-0 flex flex-col gap-8 sm:flex-row"
              >
                <div className="space-y-4 flex-1">
                  <div className="grid gap-4">
                    <div className="grid gap-2 ">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        placeholder="john@gmail.com"
                        className="placeholder:text-gray-400"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setAccountHasAccess(false);
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Please provide a valid email address. This is where
                        we'll send your order confirmation and app login
                        information.
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        className="placeholder:text-gray-400"
                      />
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="card">Card details</Label>
                        <img src={cardTypes} alt="Card types" className="h-6" />
                      </div>
                      <div className="w-full rounded-md border border-input p-2.5 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:border-gray-400 focus-visible:outline-none min-h-10">
                        <CardElement
                          options={{
                            disableLink: true,
                            style: {
                              base: {
                                fontWeight: "300",
                                fontFamily:
                                  "system-ui, -apple-system, sans-serif",
                                "::placeholder": {
                                  color: "#9ca3af",
                                },
                              },
                            },
                          }}
                          onChange={(e) => {
                            setCardComplete(e.complete);
                            setCardError(e.error ? e.error.message : null);
                            if (!e.error) {
                              setError(null);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-medium text-base">Total</span>
                      <span className="font-medium text-base">$39.00</span>
                    </div>
                    {accountHasAccess && (
                      <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm text-gray-800">
                        This email already has an active Options AI account,
                        so you don't need to pay again. Your login details
                        were emailed to you when your account was created.{" "}
                        <Link
                          to="/login"
                          className="font-medium text-purple-600 hover:underline"
                        >
                          Sign in here
                        </Link>{" "}
                        or use "Forgot password?" on the login page.
                      </div>
                    )}
                    {(error || cardError) && (
                      <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                        {error || cardError}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck
                        className="h-7 w-7 text-white flex-shrink-0 rounded-full p-1 bg-green-100"
                        strokeWidth={1.5}
                        fill={"#00A53D"}
                      />
                      <p className="text-xs text-black font-medium">
                        Secure checkout
                      </p>
                    </div>
                    <img src={stripeBlackLogo} alt="Stripe" className="h-6" />
                  </div>
                  <div className="space-y-2">
                    <Button
                      type="submit"
                      className="w-full bg-purple-600 text-white hover:bg-purple-700"
                      disabled={isProcessing}
                      variant="custom"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Please wait
                        </>
                      ) : (
                        "Pay Now"
                      )}
                    </Button>
                    <p className="text-xs text-center text-gray-500">
                      One time payment. Boost your access anytime!
                    </p>
                  </div>
                </div>
                <div className="flex-1 border sm:border-[0px] rounded-md p-4">
                  <h2 className="text-base font-medium mb-4">
                    Start Hitting Send with Confidence
                  </h2>
                  <h3 className="text-lg font-semibold">What's included</h3>
                  <ul className="grid gap-3 mt-2">
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        1 full month with your personal wingman
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        24/7 support for any girl or situation
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Checks your drafts for cool, calm, confidence
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Times your replies perfectly - stay in control
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Remembers every girl, every backstory
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Helps you stay calm during slow replies
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Love it? Boost your access any time!
                      </span>
                    </li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-4">
                    Immediate access to the App is available once payment is
                    approved.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* <div className="text-left text-sm text-gray-400 ">
        <button
          className=" font-normal hover:underline text-sm text-gray-400 "
          onClick={() => {
            signOut(auth);
            navigate("/login");
          }}
        >
          Cancel
        </button>
      </div> */}
      <div className="h-20"></div>
    </AuthLayout>
  );
}
